import { useEffect, useState } from "react";
import { PreRacePanel } from "./PreRacePanel/PreRacePanel";
import { ControlPanel } from "./ControlPanel";
import { EulerStateManager } from "../EulerStateManager";
import { StateManager } from "../GameStateManager";
import DataProvider from "./DataProvider";
import { ResultsPanel } from "./ResultsPanel/ResultsPanel";
import { RunningPanel } from "./RunningPanel/RunningPanel";
import { LoadingScreen } from "./LoadingScreen";
import { EulerStateEvents, GameStateEvents } from "../Consts";
import { RaceOrderProvider } from "./RaceOrderProvider";
import { useLocalStorage } from "usehooks-ts";
import { SUPPORTED_LANGUAGES } from "../../i18n/useTranslation";
import { LightsPanel } from "./LightsPanel";
import config from "../../../../newProject/game.config.json";

type PanelView = "markets" | "results" | "race" | null;

export function UI() {
  const [locale, setLocale] = useLocalStorage("vs_locale", "en");
  const [currentHorse, setCurrentHorse] = useState<string>("");
  const [controlsDisabled, setControlsDisabled] = useState(true);
  const [state, setState] = useState<{
    showCommentary: boolean;
    showControlsToggle: boolean;
    showControls: boolean;
    showLoadingScreen: boolean;
    showPanel: PanelView;
    showSwipeScreen: boolean;
  }>({
    showCommentary: false,
    showControlsToggle: false,
    showControls: false,
    showLoadingScreen: true,
    showPanel: null,
    showSwipeScreen: false,
  });

  function handleDisableControls() {
    setControlsDisabled(true);
  }

  function handleEnableControls() {
    setControlsDisabled(false);
  }

  function showLoadScreen() {
    setState((prevState) => ({
      ...prevState,
      showLoadingScreen: true,
      showPanel: null,
      showCommentary: false,
      showRaceOrderPanel: false,
      showControls: false,
      showControlsToggle: false,
    }));
  }

  useEffect(() => {
    if (!SUPPORTED_LANGUAGES.includes(locale)) {
      setLocale("en");
    }
    window.onmessage = function (e) {
      if (e.data) {
        if (SUPPORTED_LANGUAGES.includes(e.data)) {
          setLocale(e.data);
        } else {
          setLocale("en");
        }
      }
    };
  }, []);

  function showMarkets() {
    setState((prevState) => ({
      ...prevState,
      showPanel: "markets",
      showLoadingScreen: false,
    }));
  }

  function showWarmup() {
    setState((prevState) => ({
      ...prevState,
      showSwipeScreen: true,
      showLoadingScreen: false,
      showCommentary: true,
      showControls: true,
      showControlsToggle: true,
      showPanel: "race",
    }));
  }

  function showRaceInProgress() {
    setState((prevState) => ({
      ...prevState,
      showLoadingScreen: false,
      showRaceOrderPanel: true,
      showSwipeScreen: false,
    }));
  }

  function showResults() {
    setState((prevState) => ({
      ...prevState,
      showLoadingScreen: false,
      showPanel: "results",
      showRaceOrderPanel: false,
      showControls: false,
      showControlsToggle: false,
    }));
  }

  useEffect(() => {
    const eulerStateManager = EulerStateManager.getInstance();
    const gameStateManager = StateManager.getInstance();

    eulerStateManager.subscribeToEvent(
      EulerStateEvents.ON_ENTER_GET_NEXT_EVENT_STATE,
      showLoadScreen,
    );

    eulerStateManager.subscribeToEvent(
      EulerStateEvents.ON_ENTER_GET_NEXT_EVENT_STATE,
      handleDisableControls,
    );

    eulerStateManager.subscribeToEvent(
      EulerStateEvents.ON_ENTER_LOAD_AND_SHOW_ODDS_STATE,
      showMarkets,
    );

    gameStateManager.subscribeToEvent(
      GameStateEvents.ON_ENTER_WARM_UP_STATE,
      showWarmup,
    );

    gameStateManager.subscribeToEvent(
      GameStateEvents.ON_ENTER_WARM_UP_STATE,
      handleEnableControls,
    );

    gameStateManager.subscribeToEvent(
      GameStateEvents.ON_ENTER_RACE_IN_PROGRESS_STATE,
      showRaceInProgress,
    );

    gameStateManager.subscribeToEvent(
      GameStateEvents.ON_ENTER_POST_RACE_STATE,
      showResults,
    );
    gameStateManager.subscribeToEvent(
      GameStateEvents.ON_ENTER_POST_RACE_STATE,
      handleDisableControls,
    );

    // Cleanup function to unsubscribe from Commentary updates
    return () => {
      eulerStateManager.unsubscribeFromEvent(
        EulerStateEvents.ON_ENTER_GET_NEXT_EVENT_STATE,
        showLoadScreen,
      );

      eulerStateManager.unsubscribeFromEvent(
        EulerStateEvents.ON_ENTER_GET_NEXT_EVENT_STATE,
        handleDisableControls,
      );

      eulerStateManager.unsubscribeFromEvent(
        EulerStateEvents.ON_ENTER_LOAD_AND_SHOW_ODDS_STATE,
        showMarkets,
      );

      gameStateManager.unsubscribeFromEvent(
        GameStateEvents.ON_ENTER_WARM_UP_STATE,
        showWarmup,
      );

      gameStateManager.unsubscribeFromEvent(
        GameStateEvents.ON_ENTER_WARM_UP_STATE,
        handleEnableControls,
      );

      gameStateManager.unsubscribeFromEvent(
        GameStateEvents.ON_ENTER_RACE_IN_PROGRESS_STATE,
        showRaceInProgress,
      );
      gameStateManager.unsubscribeFromEvent(
        GameStateEvents.ON_ENTER_POST_RACE_STATE,
        showResults,
      );
      gameStateManager.unsubscribeFromEvent(
        GameStateEvents.ON_ENTER_POST_RACE_STATE,
        handleDisableControls,
      );
    };
  }, []);

  const handleStateToggle = (key: keyof typeof state) => {
    setState((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  };

  // const showControls = state.showPanel === "race";

  return (
    <DataProvider>
      <div></div>
      <RaceOrderProvider>
        <div className="fixed flex flex-col items-center justify-center w-full h-screen overflow-hidden pointer-events-none">
          <LoadingScreen showLoadingScreen={state.showLoadingScreen} />
          
          {config.sportSubType === "F1" && <LightsPanel />}
  
          <div className="relative flex flex-col flex-1 w-full gap-4">
            <div className="absolute w-full h-full sm:p-8">
              <ResultsPanel showPanel={state.showPanel === "results"} />
              <PreRacePanel showPanel={state.showPanel === "markets"} />
              <RunningPanel
                setCurrentHorse={setCurrentHorse}
                showSwipeScreen={state.showSwipeScreen}
                showCommentary={state.showCommentary}
                showPanel={state.showPanel === "race"}
              />
            </div>
          </div>
  
          <div className="flex w-full mx-auto pointer-events-auto">
            <ControlPanel
              currentHorse={currentHorse}
              setCurrentHorse={setCurrentHorse}
              showCommentary={state.showCommentary}
              isRunning={state.showPanel === "race"}
              showControls={state.showControls}
              onCommentaryClick={() => handleStateToggle("showCommentary")}
            />
          </div>
        </div>
      </RaceOrderProvider>
    </DataProvider>
  );
}
